const axios = require('axios');

function escapeRegexp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

class RequestBuilder {
    constructor(options = {}, BASE) {
        this.options = { method: 'GET', params: { filter: {} }, data: {}, headers: {}, ...options };
        this.BASE = BASE || 'https://api.reecall.cloud/api';
        this.RAW = false;
    }

    url(url) { this.options.url = url; return this; }
    route(route) { return this.url(`${this.BASE}${route}`); }
    slash(route) { return this.url(`${this.options.url}/${route}`); }
    method(method) { this.options.method = method; return this; }
    err(err) { this.err = err; return this; }
    data(data) { this.options.data = data; return this; }

    // Query data stuff
    params(params) { this.options.params = params ? { ...this.options.params, ...params } : { filter: {} }; return this; }
    filter(filter) { this.options.params.filter = filter ? { ...this.options.params.filter, ...filter } : {}; return this; }
    period(from, to, unit) { this.options.params.period = `${from.toISOString()}/${to.toISOString()}/${unit}`; return this; }
    header(name, value) { if (value !== undefined) { this.options.headers[name] = value } else { delete this.options.headers[name] }; return this; }

    fields(fields) { this.options.params.filter.fields = fields; return this; }
    include(include) { this.options.params.filter.include = include; return this; }
    where(where, replace = false) { this.options.params.filter.where = replace ? where : { ...(this.options.params.filter.where || {}), ...(where || {}) }; return this; }
    search(search, replace = false) { this.options.params.filter.search = search; return this; }
    limit(limit) { this.options.params.filter.limit = limit; return this; }
    skip(skip) { this.options.params.filter.skip = skip; return this; }
    order(order) { this.options.params.filter.order = order; return this; }

    // Additional options
    raw(r = true) { this.RAW = r; return this; }
    gm() { return this.header('x-rcll-mode', 'godmode'); }

    // Build the axios request, after calling any of the methods below, config methods won't have any effect.
    build() {
        const r = axios(this.options).then(res => this.RAW === true ? res : res.data);
        if (this.err) {
            return r.catch(error => {
                const err = new Error(this.err);
                err.error = error;
                throw err;
            });
        }
        return r;
    }

    // Promise-like stuff
    then(a, b) { return this.build().then(a, b); }
    catch(a) { return this.build().catch(a); }

    // Helper stuff
    print() { this.then(console.log, console.error) }
}

class GenericSDK {
    constructor(token, apiBase, { godmode = false } = {}) {
        this.TOKEN = token;
        this.GODMODE = godmode;
        this.API_BASE = apiBase;
    }

    setToken(token) {
        this.TOKEN = token;
    }

    _request() {
        const headers = {};
        if (this.TOKEN)
            headers.authorization = `Bearer ${this.TOKEN}`;
        if (this.GODMODE)
            headers['x-rcll-mode'] = 'godmode';
        return new RequestBuilder({ headers }, this.API_BASE);
    }
}

class SearchSDK extends GenericSDK {
    constructor(token, options = {}) {
        super(token, `${options.dev ? 'http://localhost:3126' : 'https://search.reecall.io'}/api`, options);
    }
}

class StatsSDK extends GenericSDK {
    constructor(token, options = {}) {
        super(token, `${options.dev ? 'http://localhost:3125' : 'https://stats.reecall.io'}/api`, options);
    }

    // Generic methods

    getCountByDay(model) {
        return this._request().route('/group_by_day').slash(model).err('Could not fetch group count');
    }

    getCountByPeriod(model) {
        return this._request().route('/group_by_period').slash(model).err('Could not fetch group count');
    }

    getCountByField(model, field) {
        return this._request().route('/group_by_field').slash(model).slash(field).err('Could not fetch group count');
    }

    getCounter(companyId, counterName) {
        return this._request().route('/count').slash(companyId).slash(counterName).err('Could not fetch counter');
    }

    getCount(model) {
        return this._request().route('/count').slash(model).err('Could not fetch count');
    }

    getActivityCard() {
        return this._request().route('/experimental/activity').err('Could not fetch activity card data');
    }

    getPerformanceCard() {
        return this._request().route('/experimental/performance').err('Could not fetch performance card data');
    }

    getTicketsClosedByMember() {
        return this._request().route('/experimental/tickets_closed_by_member').err('Could not fetch tickets closed by member data');
    }

    getTicketTagCategories() {
        return this._request().route('/experimental/ticket_tags_cats_count').err('Could not fetch stats data');
    }

    getTicketTags() {
        return this._request().route('/experimental/ticket_tags_count').err('Could not fetch stats data');
    }

    getTicketTagCountByDay() {
        return this._request().route('/experimental/count_ticket_tags_by_day').err('Could not fetch stats data');
    }

    getMailingData() {
        return this._request().route('/experimental/mailing').err('Could not fetch stats data');
    }

    getTagsLifecycleData() {
        return this._request().route('/experimental/tags_lifecycle').err('Could not fetch stats data');
    }

    count(companyId, name, value = 1) {
        return this._request().method('POST').route(`/count/${companyId}/${name}/${value}`).err('Could not count stats data');
    }

    getKPI() {
        return this._request().route('/experimental/kpi').err('Could not fetch kpi');
    }

    getMiscStats() {
        return this._request().route('/experimental/misc').err('Could not fetch misc stats');
    }

    // Mongo filter helpers

    dateRange(before, after) {
        const r = {};
        if (before) r.$lte = before;
        if (after) r.$gte = after;

        return r;
    }

}

class RcSDK extends GenericSDK {
    constructor(token, options = {}) {
        super(token, `${options.dev ? 'http://localhost:3090' : 'https://api.reecall.cloud'}/api`, options);
        if (options.withStats) {
            this.Stats = new StatsSDK(token, options);
        }
        if (options.withSearch !== false) {
            this.Search = new SearchSDK(token, options);
        }
    }

    // Tickets ////////////////////////////////////////////

    atlasSearchTickets(term) {
        return this.Search._request().route('/newTickets/search').search(term).err(`Could not search tickets`);
    }

    getTickets() {
        return this._request().route('/newTickets').err('Could not fetch tickets');
    }

    getTicket(id) {
        return this._request().route(`/newTickets/${id}`).err(`Could not fetch ticket with id ${id}`);
    }

    createTicket(data) {
        return this._request().method('POST').route(`/newTickets`).data(data).err(`Could not create ticket`);
    }

    updateTicket(id, data) {
        return this._request().method('PATCH').route(`/newTickets/${id}`).data(data).err(`Could not patch ticket with id ${id}`);
    }

    deleteTicket(id) {
        return this._request().method('DELETE').route(`/newTickets/${id}`).err(`Could not delete ticket with id ${id}`);
    }

    assignTicket(id, assigneeId) {
        return this._request().method('POST').route(`/newTickets/${id}/assign`).data({ assigneeId }).err(`Could not change assignee of ticket with id ${id}`);
    }

    updateTicketStatus(id, status) {
        return this._request().method('POST').route(`/newTickets/${id}/updateStatus`).data({ status }).err(`Could not change status of ticket with id ${id}`);
    }

    // Ticket Notes ///////////////////////////////////////

    createTicketNote(ticketId, companyId, content) {
        return this._request().method('POST').route(`/ticketNotes`).data({ ticketId, companyId, content }).err(`Could not create ticket note`);
    }

    updateTicketNote(id, content) {
        return this._request().method('PATCH').route(`/ticketNotes/${id}`).data({ content }).err(`Could not update ticket note with id ${id}`);
    }

    deleteTicketNote(id) {
        return this._request().method('DELETE').route(`/ticketNotes/${id}`).err(`Could not delete ticket note with id ${id}`);
    }

    // Teams //////////////////////////////////////////////

    getTeams() {
        return this._request().route('/teams').err('Could not fetch teams');
    }

    getTeam(id) {
        return this._request().route(`/teams/${id}`).err(`Could not fetch team with id ${id}`);
    }

    createTeam(data) {
        return this._request().method('POST').route(`/teams`).data(data).err(`Could not create team`);
    }

    updateTeam(id, data) {
        return this._request().method('PATCH').route(`/teams/${id}`).data(data).err(`Could not patch team with id ${id}`);
    }

    deleteTeam(id) {
        return this._request().method('DELETE').route(`/teams/${id}`).err(`Could not delete team with id ${id}`);
    }

    // Faqs ///////////////////////////////////////////////

    getFaqs() {
        return this._request().route('/faqs').err('Could not fetch faqs');
    }

    getFaq(id) {
        return this._request().route(`/faqs/${id}`).err(`Could not fetch faq with id ${id}`);
    }

    createFaq(data) {
        return this._request().method('POST').route(`/faqs`).data(data).err(`Could not create faq`);
    }

    updateFaq(id, data) {
        return this._request().method('PATCH').route(`/faqs/${id}`).data(data).err(`Could not patch faq with id ${id}`);
    }

    deleteFaq(id) {
        return this._request().method('DELETE').route(`/faqs/${id}`).err(`Could not delete faq with id ${id}`);
    }

    // Tag ////////////////////////////////////////////////

    getTags() {
        return this._request().route('/tags').err('Could not fetch tags');
    }

    getTag(id) {
        return this._request().route(`/tags/${id}`).err(`Could not fetch tag with id ${id}`)
    }

    createTag(data) {
        return this._request().method('POST').route(`/tags`).data(data).err(`Could not create tag`);
    }

    updateTag(id, data) {
        return this._request().method('PATCH').route(`/tags/${id}`).data(data).err(`Could not patch tag with id ${id}`);
    }

    deleteTag(id) {
        return this._request().method('DELETE').route(`/tags/${id}`).err(`Could not delete tag with id ${id}`);
    }

    // ShortUrl ///////////////////////////////////////////

    getShortUrls() {
        return this._request().route('/shortUrls').err('Could not fetch short urls');
    }

    getShortUrl(id) {
        return this._request().route(`/shortUrls/${id}`).err(`Could not fetch short url with id ${id}`)
    }

    createShortUrl(data) {
        return this._request().method('POST').route(`/shortUrls`).data(data).err(`Could not create short url`);
    }

    updateShortUrl(id, data) {
        return this._request().method('PATCH').route(`/shortUrls/${id}`).data(data).err(`Could not patch short url with id ${id}`);
    }

    deleteShortUrl(id) {
        return this._request().method('DELETE').route(`/shortUrls/${id}`).err(`Could not delete short url with id ${id}`);
    }

    // SmartTagData ///////////////////////////////////////

    getSmartTagDatas() {
        return this._request().route('/smartTagData').err('Could not fetch smart tag datas');
    }

    getSmartTagData(id) {
        return this._request().route(`/smartTagData/${id}`).err(`Could not fetch smart tag data with id ${id}`)
    }

    createSmartTagData(data) {
        return this._request().method('POST').route(`/smartTagData`).data(data).err(`Could not create smart tag data`);
    }

    updateSmartTagData(id, data) {
        return this._request().method('PATCH').route(`/smartTagData/${id}`).data(data).err(`Could not patch smart tag data with id ${id}`);
    }

    deleteSmartTagData(id) {
        return this._request().method('DELETE').route(`/smartTagData/${id}`).err(`Could not delete smart tag data with id ${id}`);
    }

    // Ticket Tags ////////////////////////////////////////

    getTicketTags() {
        return this._request().route('/ticketTags').err('Could not fetch ticketTags');
    }

    getTicketTag(id) {
        return this._request().route(`/ticketTags/${id}`).err(`Could not fetch ticketTag with id ${id}`);
    }

    createTicketTag(data) {
        return this._request().method('POST').route(`/ticketTags`).data(data).err(`Could not create ticketTag`);
    }

    updateTicketTag(id, data) {
        return this._request().method('PATCH').route(`/ticketTags/${id}`).data(data).err(`Could not patch ticketTag with id ${id}`);
    }

    deleteTicketTag(id) {
        return this._request().method('DELETE').route(`/ticketTags/${id}`).err(`Could not delete ticketTag with id ${id}`);
    }

    // Tags ////////////////////////////////////////

    getTags() {
        return this._request().route('/tags').err('Could not fetch tag');
    }

    getTag(id) {
        return this._request().route(`/tags/${id}`).err(`Could not fetch tag with id ${id}`);
    }

    createTag(data) {
        return this._request().method('POST').route(`/tags`).data(data).err(`Could not create tag`);
    }

    // Smart Tag Data ////////////////////////////////////////

    getSmartTagData() {
        return this._request().route('/smartTagData').err('Could not fetch smart tag data');
    }

    createSmartTagData(data) {
        return this._request().method('POST').route(`/smartTagData`).data(data).err(`Could not create smart tag data`);
    }

    // Contact Groups /////////////////////////////////////

    getContactGroups() {
        return this._request().route('/contactGroups').err('Could not fetch contactGroups');
    }

    getContactGroup(id) {
        return this._request().route(`/contactGroups/${id}`).err(`Could not fetch contactGroup with id ${id}`);
    }

    createContactGroup(data) {
        return this._request().method('POST').route(`/contactGroups`).data(data).err(`Could not create contactGroup`);
    }

    updateContactGroup(id, data) {
        return this._request().method('PATCH').route(`/contactGroups/${id}`).data(data).err(`Could not patch contactGroup with id ${id}`);
    }

    deleteContactGroup(id) {
        return this._request().method('DELETE').route(`/contactGroups/${id}`).err(`Could not delete contactGroup with id ${id}`);
    }

    // Contacts ///////////////////////////////////////////

    atlasSearchContacts(term) {
        return this.Search._request().route('/contacts/search').search(term).err(`Could not search contacts`);
    }

    getContacts() {
        return this._request().route('/contacts').err('Could not fetch contacts');
    }

    getContact(id) {
        return this._request().route(`/contacts/${id}`).err(`Could not fetch contact with id ${id}`);
    }

    gdprExportContact(id) {
        return this._request().route(`/contacts/${id}/gdprExport`).err(`Could not fetch contact gdpr data with id ${id}`);
    }

    createContact(data) {
        return this._request().method('POST').route(`/contacts`).data(data).err(`Could not create contact`);
    }

    updateContact(id, data) {
        return this._request().method('PATCH').route(`/contacts/${id}`).data(data).err(`Could not patch contact with id ${id}`);
    }

    deleteContact(id) {
        return this._request().method('DELETE').route(`/contacts/${id}`).err(`Could not delete contact with id ${id}`);
    }

    // Integration ////////////////////////////////////////

    getIntegrations() {
        return this._request().route('/integrations').err('Could not fetch integrations');
    }

    getIntegration(id) {
        return this._request().route(`/integrations/${id}`).err(`Could not fetch integration with id ${id}`);
    }

    createIntegration(data) {
        return this._request().method('POST').route(`/integrations`).data(data).err(`Could not create integration`);
    }

    updateIntegration(id, data) {
        return this._request().method('PATCH').route(`/integrations/${id}`).data(data).err(`Could not patch integration with id ${id}`);
    }

    deleteIntegration(id) {
        return this._request().method('DELETE').route(`/integrations/${id}`).err(`Could not delete integration with id ${id}`);
    }

    // quickActions ///////////////////////////////////////

    getQuickActions() {
        return this._request().route('/quickActions').err('Could not fetch quick actions');
    }

    getQuickAction(id) {
        return this._request().route(`/quickActions/${id}`).err(`Could not fetch quick action with id ${id}`);
    }

    // Integration Connections ////////////////////////////

    getIntegrationConnections() {
        return this._request().route('/integrationConnections').err('Could not fetch integration connections');
    }

    getIntegrationConnection(id) {
        return this._request().route(`/integrationConnections/${id}`).err(`Could not fetch integration connection with id ${id}`);
    }

    createIntegrationConnection(data) {
        return this._request().method('POST').route(`/integrationConnections`).data(data).err(`Could not create integration connection`);
    }

    updateIntegrationConnection(id, data) {
        return this._request().method('PATCH').route(`/integrationConnections/${id}`).data(data).err(`Could not patch integration connection with id ${id}`);
    }

    deleteIntegrationConnection(id) {
        return this._request().method('DELETE').route(`/integrationConnections/${id}`).err(`Could not delete integration connection with id ${id}`);
    }

    // Channels ///////////////////////////////////////////

    getChannels() {
        return this._request().route('/channels').err('Could not fetch channels');
    }

    getChannel(id) {
        return this._request().route(`/channels/${id}`).err(`Could not fetch channel with id ${id}`);
    }

    createChannel(data) {
        return this._request().method('POST').route(`/channels`).data(data).err(`Could not create channel`);
    }

    updateChannel(id, data) {
        return this._request().method('PATCH').route(`/channels/${id}`).data(data).err(`Could not patch channel with id ${id}`);
    }

    deleteChannel(id) {
        return this._request().method('DELETE').route(`/channels/${id}`).err(`Could not delete channel with id ${id}`);
    }

    // ChannelConnections ///////////////////////////////////////////

    getChannelConnections() {
        return this._request().route('/channelConnections').err('Could not fetch channel connections');
    }

    getChannelConnection(id) {
        return this._request().route(`/channelConnections/${id}`).err(`Could not fetch channel connection with id ${id}`);
    }

    createChannelConnection(data) {
        return this._request().method('POST').route(`/channelConnections`).data(data).err(`Could not create channel connection`);
    }

    updateChannelConnection(id, data) {
        return this._request().method('PATCH').route(`/channelConnections/${id}`).data(data).err(`Could not patch channel connection with id ${id}`);
    }

    deleteChannelConnection(id) {
        return this._request().method('DELETE').route(`/channelConnections/${id}`).err(`Could not delete channel connection with id ${id}`);
    }

    // Agents ///////////////////////////////////////////

    getAgents() {
        return this._request().route('/agents').err('Could not fetch agents');
    }

    getAgent(id) {
        return this._request().route(`/agents/${id}`).err(`Could not fetch agent with id ${id}`);
    }

    createAgent(data) {
        return this._request().method('POST').route(`/agents`).data(data).err(`Could not create agent`);
    }

    updateAgent(id, data) {
        return this._request().method('PATCH').route(`/agents/${id}`).data(data).err(`Could not patch agent with id ${id}`);
    }

    deleteAgent(id) {
        return this._request().method('DELETE').route(`/agents/${id}`).err(`Could not delete agent with id ${id}`);
    }

    // Templates //////////////////////////////////////////

    getTemplates() {
        return this._request().route('/templates').err('Could not fetch templates');
    }

    getTemplate(id) {
        return this._request().route(`/templates/${id}`).err(`Could not fetch template with id ${id}`);
    }

    getPageTemplateBySlug(companyId, slug) {
        return this._request().route('/templates/findPageTemplateBySlug').slash(companyId).slash(slug).err(`Could not fetch page template with companyId and slug pair`);
    }

    createTemplate(data) {
        return this._request().method('POST').route(`/templates`).data(data).err(`Could not create template`);
    }

    updateTemplate(id, data) {
        return this._request().method('PATCH').route(`/templates/${id}`).data(data).err(`Could not patch template with id ${id}`);
    }

    deleteTemplate(id) {
        return this._request().method('DELETE').route(`/templates/${id}`).err(`Could not delete template with id ${id}`);
    }

    // Collection /////////////////////////////////////////

    getCollections() {
        return this._request().route('/collections').err('Could not fetch collections');
    }

    getCollection(id) {
        return this._request().route(`/collections/${id}`).err(`Could not fetch collection with id ${id}`);
    }

    exportCollection(id) {
        return this._request().route(`/collections/${id}/export`).err(`Could not export collection with id ${id}`);
    }

    createCollection(data) {
        return this._request().method('POST').route(`/collections`).data(data).err(`Could not create collection`);
    }

    updateCollection(id, data) {
        return this._request().method('PATCH').route(`/collections/${id}`).data(data).err(`Could not patch collection with id ${id}`);
    }

    deleteCollection(id) {
        return this._request().method('DELETE').route(`/collections/${id}`).err(`Could not delete collection with id ${id}`);
    }

    // CollectionData /////////////////////////////////////

    getCollectionDatas() {
        return this._request().route('/collectionData').err('Could not fetch collection datas');
    }

    getCollectionData(id) {
        return this._request().route(`/collectionData/${id}`).err(`Could not fetch collection data with id ${id}`);
    }

    createCollectionData(data) {
        return this._request().method('POST').route(`/collectionData`).data(data).err(`Could not create collection data`);
    }

    updateCollectionData(id, data) {
        return this._request().method('PATCH').route(`/collectionData/${id}`).data(data).err(`Could not patch collection data with id ${id}`);
    }

    deleteCollectionData(id) {
        return this._request().method('DELETE').route(`/collectionData/${id}`).err(`Could not delete collection data with id ${id}`);
    }

    // Conversations //////////////////////////////////////

    atlasSearchConversations(term) {
        return this.Search._request().route('/conversations/search').search(term).err(`Could not search conversations`);
    }

    getConversations() {
        return this._request().route('/conversations').err('Could not fetch conversations');
    }

    getConversation(id) {
        return this._request().route(`/conversations/${id}`).err(`Could not fetch conversation with id ${id}`);
    }

    updateConversation(id, data) {
        return this._request().method('PATCH').route(`/conversations/${id}`).data(data).err(`Could not update conversation with id ${id}`);
    }

    setConversationTicket(id, ticketId) {
        return this.updateConversation(id, { ticketId });
    }

    setConversationContact(id, contactId) {
        return this.updateConversation(id, { contactId });
    }

    editConversationContact(id, data) {
        return this._request().method('POST').route(`/conversations/${id}/editContact`).data(data).err(`Could not edit contact of conversation with id ${id}`);
    }

    editConversationData(id, data) {
        return this._request().method('POST').route(`/conversations/${id}/editData`).data(data).err(`Could not edit data of conversation with id ${id}`);
    }

    editConversationTags(id, data) {
        return this._request().method('POST').route(`/conversations/${id}/editTags`).data(data).err(`Could not confirm tags of conversation with id ${id}`);
    }

    // ACD ////////////////////////////////////////////////

    getACDs() {
        return this._request().route('/ACDs').err('Could not fetch acds');
    }

    getACD(id) {
        return this._request().route(`/ACDs/${id}`).err(`Could not fetch acd with id ${id}`)
    }

    createACD(data) {
        return this._request().method('POST').route(`/ACDs`).data(data).err(`Could not create acd`);
    }

    updateACD(id, data) {
        return this._request().method('PATCH').route(`/ACDs/${id}`).data(data).err(`Could not patch acd with id ${id}`);
    }

    deleteACD(id) {
        return this._request().method('DELETE').route(`/ACDs/${id}`).err(`Could not delete acd with id ${id}`);
    }

    // Company Settings ///////////////////////////////////

    getCompanySettings() {
        return this._request().route('/companySettings').err('Could not fetch company settings');
    }

    async getCompanySetting(companyId) {
        const [set] = await this._request().route(`/companySettings`).where({ companyId }).err(`Could not fetch companySetting with company id ${companyId}`)
        if (set) return set;
        throw new Error(`Could not find companySetting with companyId ${companyId}`);
    }

    createCompanySetting(companyId, value = {}) {
        return this._request().method('POST').route(`/companySettings`).data({ value, companyId }).err(`Could not create companySetting`);
    }

    updateCompanySetting(id, value) {
        return this._request().method('PATCH').route(`/companySettings/${id}`).data({ value }).err(`Could not patch companySetting with id ${id}`);
    }

    getOrCreateCompanySetting(companyId, defaultValue = {}) {
        return this.getCompanySetting(companyId).catch(err => {
            return this.createCompanySetting(companyId, defaultValue);
        });
    }

    // Decks //////////////////////////////////////////////

    getDecks() {
        return this._request().route('/decks').err('Could not fetch decks');
    }

    getDeck(id) {
        return this._request().route(`/decks/${id}`).err(`Could not fetch deck with id ${id}`);
    }

    createDeck(data) {
        return this._request().method('POST').route(`/decks`).data(data).err(`Could not create deck`);
    }

    updateDeck(id, data) {
        return this._request().method('PATCH').route(`/decks/${id}`).data(data).err(`Could not patch deck with id ${id}`);
    }

    deleteDeck(id) {
        return this._request().method('DELETE').route(`/decks/${id}`).err(`Could not delete deck with id ${id}`);
    }

    // Members ////////////////////////////////////////////

    getMembers() {
        return this._request().route('/members').err('Could not fetch members');
    }

    getMember(id) {
        return this._request().route(`/members/${id}`).err(`Could not fetch member with id ${id}`);
    }

    createMember(data) {
        return this._request().method('POST').route(`/members`).data(data).err(`Could not create member`);
    }

    updateMember(id, data) {
        return this._request().method('PATCH').route(`/members/${id}`).data(data).err(`Could not patch member with id ${id}`);
    }

    deleteMember(id) {
        return this._request().method('DELETE').route(`/members/${id}`).err(`Could not delete member with id ${id}`);
    }

    // Membership /////////////////////////////////////////

    getMemberships() {
        return this._request().route('/memberships').err('Could not fetch memberships');
    }

    getMembership(id) {
        return this._request().route(`/memberships/${id}`).err(`Could not fetch membership with id ${id}`)
    }

    createMembership(data) {
        return this._request().method('POST').route(`/memberships`).data(data).err(`Could not create membership`);
    }

    updateMembership(id, data) {
        return this._request().method('PATCH').route(`/memberships/${id}`).data(data).err(`Could not patch membership with id ${id}`);
    }

    deleteMembership(id) {
        return this._request().method('DELETE').route(`/memberships/${id}`).err(`Could not delete membership with id ${id}`);
    }

    // Companies //////////////////////////////////////////

    getCompanies() {
        return this._request().route('/companies').err('Could not fetch companies');
    }

    getCompany(id) {
        return this._request().route(`/companies/${id}`).err(`Could not fetch company with id ${id}`);
    }

    createCompany(data) {
        return this._request().method('POST').route(`/companies`).data(data).err(`Could not create company`);
    }

    updateCompany(id, data) {
        return this._request().method('PATCH').route(`/companies/${id}`).data(data).err(`Could not patch company with id ${id}`);
    }

    deleteCompany(id) {
        return this._request().method('DELETE').route(`/companies/${id}`).err(`Could not delete company with id ${id}`);
    }

    getCompanyMembers(companyId) {
        return this._request().route(`/companies/${companyId}/members`).err(`Could not fetch members for company with id ${companyId}`);
    }

    exportCompanyData(companyId) {
        return this._request().route(`/companies/${companyId}/export`).err(`Could not export data for company with id ${companyId}`);
    }

    registerCompany(data) {
        return this._request().method('POST').route(`/companies/register`).data(data).err(`Could not create company`);
    }

    // FaqCategory ////////////////////////////////////////

    getFaqCategories() {
        return this._request().route('/faqCategories').err('Could not fetch faq categories');
    }

    getFaqCategory(id) {
        return this._request().route(`/faqCategories/${id}`).err(`Could not fetch faq category with id ${id}`)
    }

    createFaqCategory(data) {
        return this._request().method('POST').route(`/faqCategories`).data(data).err(`Could not create faq category`);
    }

    updateFaqCategory(id, data) {
        return this._request().method('PATCH').route(`/faqCategories/${id}`).data(data).err(`Could not patch faq category with id ${id}`);
    }

    deleteFaqCategory(id) {
        return this._request().method('DELETE').route(`/faqCategories/${id}`).err(`Could not delete faq category with id ${id}`);
    }

    // QuickActionLibrary /////////////////////////////////

    getQuickActionLibraries() {
        return this._request().route('/quickActionLibraries').err('Could not fetch quick action libraries');
    }

    getQuickActionLibrary(id) {
        return this._request().route(`/quickActionLibraries/${id}`).err(`Could not fetch quick action library with id ${id}`)
    }

    createQuickActionLibrary(data) {
        return this._request().method('POST').route(`/quickActionLibraries`).data(data).err(`Could not create quick action library`);
    }

    updateQuickActionLibrary(id, data) {
        return this._request().method('PATCH').route(`/quickActionLibraries/${id}`).data(data).err(`Could not patch quick action library with id ${id}`);
    }

    deleteQuickActionLibrary(id) {
        return this._request().method('DELETE').route(`/quickActionLibraries/${id}`).err(`Could not delete quick action library with id ${id}`);
    }

    // HelpText ///////////////////////////////////////////

    getHelpTexts() {
        return this._request().route('/helpTexts').err('Could not fetch help texts');
    }

    getHelpText(id) {
        return this._request().route(`/helpTexts/${id}`).err(`Could not fetch help text with id ${id}`)
    }

    createHelpText(data) {
        return this._request().method('POST').route(`/helpTexts`).data(data).err(`Could not create help text`);
    }

    updateHelpText(id, data) {
        return this._request().method('PATCH').route(`/helpTexts/${id}`).data(data).err(`Could not patch help text with id ${id}`);
    }

    deleteHelpText(id) {
        return this._request().method('DELETE').route(`/helpTexts/${id}`).err(`Could not delete help text with id ${id}`);
    }

    getHelpTextBySlug(pageSlug, sectionSlug) {
        return this._request().route(`/helpTexts/findBySlug/${pageSlug}/${sectionSlug}`).err(`Could not fetch help text with pageSlug sectionSlug pair`)
    }

    // Rule ///////////////////////////////////////////////

    getRules() {
        return this._request().route('/rules').err('Could not fetch rules');
    }

    getRule(id) {
        return this._request().route(`/rules/${id}`).err(`Could not fetch rule with id ${id}`)
    }

    createRule(data) {
        return this._request().method('POST').route(`/rules`).data(data).err(`Could not create rule`);
    }

    updateRule(id, data) {
        return this._request().method('PATCH').route(`/rules/${id}`).data(data).err(`Could not patch rule with id ${id}`);
    }

    deleteRule(id) {
        return this._request().method('DELETE').route(`/rules/${id}`).err(`Could not delete rule with id ${id}`);
    }

    // RuleStep ///////////////////////////////////////////

    getRuleSteps() {
        return this._request().route('/ruleSteps').err('Could not fetch rule steps');
    }

    getRuleStep(id) {
        return this._request().route(`/ruleSteps/${id}`).err(`Could not fetch rule step with id ${id}`)
    }

    createRuleStep(data) {
        return this._request().method('POST').route(`/ruleSteps`).data(data).err(`Could not create rule step`);
    }

    updateRuleStep(id, data) {
        return this._request().method('PATCH').route(`/ruleSteps/${id}`).data(data).err(`Could not patch rule step with id ${id}`);
    }

    deleteRuleStep(id) {
        return this._request().method('DELETE').route(`/ruleSteps/${id}`).err(`Could not delete rule step with id ${id}`);
    }

    // RuleCondition //////////////////////////////////////

    getRuleConditions() {
        return this._request().route('/ruleConditions').err('Could not fetch rule conditions');
    }

    getRuleCondition(id) {
        return this._request().route(`/ruleConditions/${id}`).err(`Could not fetch rule condition with id ${id}`)
    }

    createRuleCondition(data) {
        return this._request().method('POST').route(`/ruleConditions`).data(data).err(`Could not create rule condition`);
    }

    updateRuleCondition(id, data) {
        return this._request().method('PATCH').route(`/ruleConditions/${id}`).data(data).err(`Could not patch rule condition with id ${id}`);
    }

    deleteRuleCondition(id) {
        return this._request().method('DELETE').route(`/ruleConditions/${id}`).err(`Could not delete rule condition with id ${id}`);
    }

    // TicketTagData //////////////////////////////////////

    getTicketTagDatas() {
        return this._request().route('/ticketTagData').err('Could not fetch ticket tag data');
    }

    getTicketTagData(id) {
        return this._request().route(`/ticketTagData/${id}`).err(`Could not fetch ticket tag data with id ${id}`)
    }

    createTicketTagData(data) {
        return this._request().method('POST').route(`/ticketTagData`).data(data).err(`Could not create ticket tag data`);
    }

    updateTicketTagData(id, data) {
        return this._request().method('PATCH').route(`/ticketTagData/${id}`).data(data).err(`Could not patch ticket tag data with id ${id}`);
    }

    deleteTicketTagData(id) {
        return this._request().method('DELETE').route(`/ticketTagData/${id}`).err(`Could not delete ticket tag data with id ${id}`);
    }

    // IntegrationConfiguration ///////////////////////////

    getIntegrationConfigurations() {
        return this._request().route('/integrationConfigurations').err('Could not fetch integration configurations');
    }

    getIntegrationConfiguration(id) {
        return this._request().route(`/integrationConfigurations/${id}`).err(`Could not fetch integration configuration with id ${id}`)
    }

    createIntegrationConfiguration(data) {
        return this._request().method('POST').route(`/integrationConfigurations`).data(data).err(`Could not create integration configuration`);
    }

    updateIntegrationConfiguration(id, data) {
        return this._request().method('PATCH').route(`/integrationConfigurations/${id}`).data(data).err(`Could not patch integration configuration with id ${id}`);
    }

    deleteIntegrationConfiguration(id) {
        return this._request().method('DELETE').route(`/integrationConfigurations/${id}`).err(`Could not delete integration configuration with id ${id}`);
    }

    // Hook ///////////////////////////////////////////////

    getHooks() {
        return this._request().route('/hooks').err('Could not fetch hooks');
    }

    getHook(id) {
        return this._request().route(`/hooks/${id}`).err(`Could not fetch hook with id ${id}`)
    }

    createHook(data) {
        return this._request().method('POST').route(`/hooks`).data(data).err(`Could not create hook`);
    }

    updateHook(id, data) {
        return this._request().method('PATCH').route(`/hooks/${id}`).data(data).err(`Could not patch hook with id ${id}`);
    }

    deleteHook(id) {
        return this._request().method('DELETE').route(`/hooks/${id}`).err(`Could not delete hook with id ${id}`);
    }

    // BillingSetting /////////////////////////////////////

    getBillingSettings() {
        return this._request().route('/billingSettings').err('Could not fetch billing settings');
    }

    getBillingSetting(id) {
        return this._request().route(`/billingSettings/${id}`).err(`Could not fetch billing setting with id ${id}`)
    }

    createBillingSetting(data) {
        return this._request().method('POST').route(`/billingSettings`).data(data).err(`Could not create billing setting`);
    }

    updateBillingSetting(id, data) {
        return this._request().method('PATCH').route(`/billingSettings/${id}`).data(data).err(`Could not patch billing setting with id ${id}`);
    }

    deleteBillingSetting(id) {
        return this._request().method('DELETE').route(`/billingSettings/${id}`).err(`Could not delete billing setting with id ${id}`);
    }

    // ExportConfiguration ////////////////////////////////

    getExportConfigurations() {
        return this._request().route('/exportConfigurations').err('Could not fetch export configurations');
    }

    getExportConfiguration(id) {
        return this._request().route(`/exportConfigurations/${id}`).err(`Could not fetch export configuration with id ${id}`)
    }

    createExportConfiguration(data) {
        return this._request().method('POST').route(`/exportConfigurations`).data(data).err(`Could not create export configuration`);
    }

    updateExportConfiguration(id, data) {
        return this._request().method('PATCH').route(`/exportConfigurations/${id}`).data(data).err(`Could not patch export configuration with id ${id}`);
    }

    deleteExportConfiguration(id) {
        return this._request().method('DELETE').route(`/exportConfigurations/${id}`).err(`Could not delete export configuration with id ${id}`);
    }

    // ExportLog //////////////////////////////////////////

    getExportLogs() {
        return this._request().route('/exportLogs').err('Could not fetch export logs');
    }

    getExportLog(id) {
        return this._request().route(`/exportLogs/${id}`).err(`Could not fetch export log with id ${id}`)
    }

    createExportLog(data) {
        return this._request().method('POST').route(`/exportLogs`).data(data).err(`Could not create export log`);
    }

    updateExportLog(id, data) {
        return this._request().method('PATCH').route(`/exportLogs/${id}`).data(data).err(`Could not patch export log with id ${id}`);
    }

    deleteExportLog(id) {
        return this._request().method('DELETE').route(`/exportLogs/${id}`).err(`Could not delete export log with id ${id}`);
    }

    // Search /////////////////////////////////////////////

    searchConversations(query, where = {}) {
        return this.getConversations().where({
            and: [
                where,
                {
                    or: [
                        { 'messages.message': query },
                        { 'collectedData.value': query },
                        { 'title': query },
                    ]
                }
            ]
        });
    }

    async searchTickets(query, where = {}) {
        const tIds = (await this.searchConversations(query).fields(['ticketId'])).map(({ ticketId }) => ticketId).filter(a => a);
        // console.log('tickets', tIds);
        const tagIds = (await this.getTicketTags().where({ name: query }).fields(['id'])).map(({ id }) => `tid:${id}`).filter(a => a);
        // console.log('tags', tagIds);

        return () => this.getTickets().where({
            and: [
                where,
                {
                    or: [
                        { title: query },
                        { id: { inq: tIds } },
                        { tagIds: { inq: tagIds } }
                    ]
                }
            ]
        });
    }

    // LibraryPage ////////////////////////////////////////

    getLibraryPages() {
        return this._request().route('/libraryPages').err('Could not fetch library pages');
    }

    getLibraryPage(id) {
        return this._request().route(`/libraryPages/${id}`).err(`Could not fetch library page with id ${id}`)
    }

    createLibraryPage(data) {
        return this._request().method('POST').route(`/libraryPages`).data(data).err(`Could not create library page`);
    }

    updateLibraryPage(id, data) {
        return this._request().method('PATCH').route(`/libraryPages/${id}`).data(data).err(`Could not patch library page with id ${id}`);
    }

    deleteLibraryPage(id) {
        return this._request().method('DELETE').route(`/libraryPages/${id}`).err(`Could not delete library page with id ${id}`);
    }

    // Dashboard //////////////////////////////////////////

    getDashboards() {
        return this._request().route('/dashboards').err('Could not fetch dashboards');
    }

    getDashboard(id) {
        return this._request().route(`/dashboards/${id}`).err(`Could not fetch dashboard with id ${id}`)
    }

    createDashboard(data) {
        return this._request().method('POST').route(`/dashboards`).data(data).err(`Could not create dashboard`);
    }

    updateDashboard(id, data) {
        return this._request().method('PATCH').route(`/dashboards/${id}`).data(data).err(`Could not patch dashboard with id ${id}`);
    }

    deleteDashboard(id) {
        return this._request().method('DELETE').route(`/dashboards/${id}`).err(`Could not delete dashboard with id ${id}`);
    }

        // Invitation /////////////////////////////////////////

        getInvitations() {
            return this._request().route('/invitations').err('Could not fetch invitations');
        }
    
        getInvitation(id) {
            return this._request().route(`/invitations/${id}`).err(`Could not fetch invitation with id ${id}`)
        }
    
        createInvitation(data) {
            return this._request().method('POST').route(`/invitations`).data(data).err(`Could not create invitation`);
        }
    
        updateInvitation(id, data) {
            return this._request().method('PATCH').route(`/invitations/${id}`).data(data).err(`Could not patch invitation with id ${id}`);
        }
    
        deleteInvitation(id) {
            return this._request().method('DELETE').route(`/invitations/${id}`).err(`Could not delete invitation with id ${id}`);
        }

    // Self infos /////////////////////////////////////////

    async getMyself() {
        const member = await this._request().route('/members/me').err('Could not fetch yourself');
        const permissions = await this._request().route(`/members/${member.id}/permissions`).err('Could not fetch permissions');

        return { ...member, permissions };
    }
}

RcSDK.StatsSDK = StatsSDK;
module.exports = RcSDK;